import { ArrowLeft, FlaskConical, BookOpen, ExternalLink } from 'lucide-react';

/**
 * /blog/spa-fallback-false-positives
 *
 * Practitioner note. Short, opinionated, with concrete code. No academic
 * framing — written for engineers who hit this in their own scanners or
 * SPA hosting setups.
 */
export function SpaFallbackFalsePositivesPost() {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden">
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-halo-turmeric opacity-60" />
      </div>

      <nav className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-5 max-w-3xl mx-auto gap-4">
        <a href="/" className="flex items-center gap-2.5 shrink-0">
          <FlaskConical className="h-6 w-6 text-saffron" />
          <span className="font-display text-lg sm:text-xl font-semibold tracking-wide">
            Nirvik <span className="text-saffron">Research</span>
          </span>
        </a>
        <a
          href="/#writing"
          className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All writing
        </a>
      </nav>

      <article className="relative z-10 px-5 sm:px-6 py-12 sm:py-16 max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <BookOpen className="h-4 w-4 text-saffron" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Practitioner notes</span>
          <span className="text-xs text-muted-foreground/70 ml-2">2026-04-26</span>
        </div>

        <h1 className="font-display text-3xl sm:text-5xl font-semibold tracking-tight leading-[1.08] mb-6 bg-gradient-to-br from-foreground via-saffron to-primary-strong bg-clip-text text-transparent">
          The SPA-fallback false positive — and how to close it on both sides
        </h1>

        <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-10">
          Most webapp pentest tools probe a list of sensitive paths — <code className="text-saffron">.env</code>,{' '}
          <code className="text-saffron">.git/config</code>, <code className="text-saffron">/admin</code>,{' '}
          <code className="text-saffron">/wp-config.php.bak</code> — and flag any{' '}
          <code className="text-saffron">200 OK</code> as a leak. On a single-page app, every one of those
          requests returns 200 OK with the app shell. Every probe becomes a finding. Every finding is wrong.
        </p>

        <p className="text-base sm:text-lg leading-relaxed mb-6">
          We hit this running our own webapp-pentest agent against an SPA target. The signal-to-noise ratio
          collapsed: the report was 90% false positives, all driven by a CloudFront fallback that mapped any
          unknown path to <code>/index.html</code> with status 200. Two fixes shipped — one in the agent, one
          in our own hosting — and they are both worth describing because the second is the more interesting
          half.
        </p>

        <h2 className="font-display text-xl sm:text-2xl font-semibold mt-12 mb-4 text-foreground">Where the false positive comes from</h2>

        <p className="text-base leading-relaxed mb-4">
          Single-page apps need every URL the user might deep-link to (<code>/dashboard</code>,{' '}
          <code>/projects/42</code>, <code>/settings</code>) to load <code>index.html</code> so the
          client-side router can take over. The standard pattern on every static-site host — CloudFront, Netlify,
          Vercel, Cloudflare Pages, Firebase Hosting — is the same: rewrite unknown paths to{' '}
          <code>/index.html</code>, return 200.
        </p>

        <p className="text-base leading-relaxed mb-4">
          That works perfectly for the user. It also means that every probe path a scanner sends — including
          paths the SPA's router has never heard of — gets a clean 200 response with HTML. Status-only checks
          can't tell <em>"this is a real leak"</em> from <em>"this is the SPA shell."</em> The two responses
          are byte-for-byte identical.
        </p>

        <h2 className="font-display text-xl sm:text-2xl font-semibold mt-12 mb-4 text-foreground">Fix one — make the scanner smarter</h2>

        <p className="text-base leading-relaxed mb-4">
          The scanner side is the broader fix because we can't assume the target host is configured well.
          Two complementary signals close most of the gap:
        </p>

        <h3 className="font-display text-lg font-semibold mt-8 mb-3">Calibration probe</h3>
        <p className="text-base leading-relaxed mb-4">
          Before the real probes, hit <em>one</em> guaranteed-bogus path —{' '}
          <code>/__nirvik_probe_404_${'<'}random{'>'}</code> — and capture the response: status, content-type,
          length, and the first 256 bytes. Compare every subsequent probe response to that fingerprint. If
          status, content-type, and body shape all match, it's the fallback. Treat it as not-found regardless
          of the status code on the wire.
        </p>

        <pre className="bg-muted/60 border border-border rounded-xl p-4 text-xs sm:text-sm overflow-x-auto mb-4 leading-relaxed">
{`// pseudocode
const cal = await calibrate(\`/__nirvik_probe_404_\${rand()}\`);
// cal = { status: 200, length: 1417, contentType: 'text/html', bodyHead: '<!doctype html>...' }

for (const path of SENSITIVE_PATHS) {
  const res = await fetch(path);
  const body = await res.text();
  if (matchesFallback(res, body, cal)) continue; // suppress
  // ...real finding
}`}
        </pre>

        <h3 className="font-display text-lg font-semibold mt-8 mb-3">Content-type mismatch</h3>
        <p className="text-base leading-relaxed mb-4">
          Calibration handles the common case but won't catch responses that differ slightly per request
          (CSRF tokens, request IDs in the body). Layer in a type heuristic: paths that, if real, never serve
          HTML — <code>.env</code>, <code>.git/*</code>, <code>.sql</code>, <code>.bak</code>,{' '}
          <code>/actuator/*</code>, <code>phpinfo.php</code> — should be suppressed when the response{' '}
          <em>is</em> HTML. A real <code>.env</code> is <code>text/plain</code>. HTML on those paths is a
          fallback tell.
        </p>

        <p className="text-base leading-relaxed mb-4">
          Together these two filters drop the false-positive rate to near zero on SPA targets without
          missing real leaks. Calibration costs one extra HTTP request per host. The type check costs nothing.
        </p>

        <h2 className="font-display text-xl sm:text-2xl font-semibold mt-12 mb-4 text-foreground">Fix two — return real 404s on your own SPA</h2>

        <p className="text-base leading-relaxed mb-4">
          The scanner-side fix protects <em>us</em> from <em>their</em> hosting choices. The host-side fix
          protects everyone. The pattern most SPAs ship — fallback every unknown path to <code>index.html</code>{' '}
          — leaks information. Probing tools, search engine crawlers, and bug-bounty hunters all parse 200{' '}
          OK as "exists." Every <code>.env</code> probe says yes. Every dotfile probe says yes.
        </p>

        <p className="text-base leading-relaxed mb-4">
          The fix is a build-time route allowlist baked into the edge function. The SPA has a finite set of
          top-level segments — for our app that's <code>labs</code>, <code>login</code>, <code>signup</code>,{' '}
          <code>dashboard</code>, <code>projects</code>, <code>learn</code>, <code>onboarding</code>,{' '}
          <code>settings</code>. Anything else doesn't get the app shell. It gets 404.
        </p>

        <pre className="bg-muted/60 border border-border rounded-xl p-4 text-xs sm:text-sm overflow-x-auto mb-4 leading-relaxed">
{`// CloudFront Function (viewer-request)
var ALLOWED = ['labs','login','signup','dashboard','projects','learn','onboarding','settings'];

function handler(event) {
  var uri = event.request.uri;
  if (uri === '/') return event.request;

  var seg = uri.split('/')[1] || '';
  if (ALLOWED.indexOf(seg) !== -1) {
    event.request.uri = '/index.html';   // SPA route → app shell
    return event.request;
  }
  if (uri.indexOf('.') !== -1) return event.request;  // asset → S3

  return {                                              // unknown → real 404
    statusCode: 404,
    statusDescription: 'Not Found',
    headers: { 'content-type': { value: 'text/plain' } },
    body: 'Not Found',
  };
}`}
        </pre>

        <p className="text-base leading-relaxed mb-4">
          A few things to note about the shape of this:
        </p>

        <ul className="list-disc pl-6 space-y-2 text-base leading-relaxed mb-4 marker:text-saffron">
          <li>
            <strong className="text-foreground">React Router still owns the sub-tree.</strong> Allowlisting{' '}
            <code>projects</code> covers <code>/projects</code>, <code>/projects/new</code>,{' '}
            <code>/projects/42</code>, <code>/projects/42/agents/runs/abc</code> — the SPA renders all of them.
          </li>
          <li>
            <strong className="text-foreground">Static assets pass through.</strong> Anything with a{' '}
            <code>.</code> in the URI (favicon, JS bundle, CSS, images) hits S3 directly. Missing assets get
            real 404s, not the app shell.
          </li>
          <li>
            <strong className="text-foreground">No more error-response rewrites.</strong> The classic{' '}
            <code>404 → 200 + /index.html</code> trick is what created the loophole. With the allowlist in
            place, that block can be removed entirely.
          </li>
          <li>
            <strong className="text-foreground">The list lives next to the router.</strong> When a new
            top-level route ships in <code>App.tsx</code>, the allowlist needs the same segment. Generate it at
            build time if you can't trust humans to remember.
          </li>
        </ul>

        <h2 className="font-display text-xl sm:text-2xl font-semibold mt-12 mb-4 text-foreground">Both fixes matter</h2>

        <p className="text-base leading-relaxed mb-4">
          The agent fix is the one we ship to every customer — they don't control how their target apps are
          hosted, and we can't audit every CloudFront / Netlify / Cloudflare config out there. Calibration plus
          content-type checks make us correct on whatever the wire returns.
        </p>

        <p className="text-base leading-relaxed mb-10">
          The hosting fix is the one we ship to ourselves, and we'd recommend it to anyone running a static SPA
          behind a CDN. Returning real 404s is a small change with disproportionate signal-to-noise impact:
          no more spurious leak claims, no more false-positive bug-bounty submissions, and a tighter signal for
          any tool — ours or anyone else's — trying to fingerprint your attack surface.
        </p>

        <div className="border-t border-border pt-8 mt-12">
          <p className="text-sm text-muted-foreground">
            Questions or feedback?{' '}
            <a
              href="mailto:research@nirvik.ai"
              className="text-saffron hover:text-foreground underline underline-offset-4 decoration-saffron/40 inline-flex items-center gap-1"
            >
              research@nirvik.ai
              <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </div>
      </article>

      <div className="relative z-10 flex">
        <div className="h-0.5 flex-1 bg-saffron" />
        <div className="h-0.5 flex-1 bg-white" />
      </div>

      <footer className="relative z-10 px-5 sm:px-6 py-10 border-t border-border/50">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <FlaskConical className="h-5 w-5 text-saffron" />
            <span className="font-display text-sm font-semibold tracking-wide text-muted-foreground">Nirvik Research</span>
          </div>
          <p className="text-xs text-muted-foreground text-center sm:text-right">
            Open research. Practical defense. &copy; 2026 Nirvik AI Labs.
          </p>
        </div>
      </footer>
    </div>
  );
}
